'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import { Search, Bell, Globe, ChevronDown, LogOut, Settings } from 'lucide-react';
import { useRole } from '@/context/RoleContext';

const languages = [
  { code: 'EN', label: 'English' },
  { code: 'MY', label: 'Bahasa Melayu' },
  { code: 'AR', label: 'العربية' },
  { code: 'ZH', label: '中文' },
];

const ROLE_NOTIFICATIONS: Record<string, { id: string; text: string; time: string }[]> = {
  sme: [
    { id: 'n1', text: 'Your halal certificate was verified successfully.', time: '5 min ago' },
    { id: 'n2', text: 'Gulf Retail Group requested quotation for Premium Frozen Beef.', time: '22 min ago' },
    { id: 'n3', text: 'Amanah Islamic Finance Partner reviewed your funding request.', time: '2 hr ago' },
    { id: 'n4', text: 'Your company profile reached 94% readiness.', time: '4 hr ago' },
  ],
  buyer: [
    { id: 'n1', text: 'Nusantara Halal Foods responded to your quotation request.', time: '8 min ago' },
    { id: 'n2', text: 'Certificate HC-MY-2026-004582 verified successfully.', time: '30 min ago' },
    { id: 'n3', text: 'Three new halal cosmetics suppliers match your sourcing interest.', time: '1 hr ago' },
    { id: 'n4', text: 'Business matching meeting confirmed for Malaysia Halal Investment Week.', time: '3 hr ago' },
  ],
  investor: [
    { id: 'n1', text: 'Investor pack requested for Halal Food Manufacturing Expansion.', time: '10 min ago' },
    { id: 'n2', text: 'Shariah review completed for OIC Agriculture Project.', time: '45 min ago' },
    { id: 'n3', text: 'New US halal cosmetics opportunity added.', time: '2 hr ago' },
    { id: 'n4', text: 'Certificate verified for Nusantara Halal Foods.', time: '5 hr ago' },
  ],
  certifier: [
    { id: 'n1', text: 'Certificate HC-US-2026-001129 updated as valid.', time: '3 min ago' },
    { id: 'n2', text: 'New SME submitted halal certification documents.', time: '20 min ago' },
    { id: 'n3', text: 'Audit trail completed for Nusantara Halal Foods.', time: '1 hr ago' },
    { id: 'n4', text: 'QR verification hash generated successfully.', time: '3 hr ago' },
  ],
  finance: [
    { id: 'n1', text: 'Global Tayyib Foods matched with Murabahah trade financing.', time: '15 min ago' },
    { id: 'n2', text: 'Takaful request received for GCC export shipment.', time: '40 min ago' },
    { id: 'n3', text: 'One SME approved in principle.', time: '2 hr ago' },
    { id: 'n4', text: 'Additional documents requested from China supplier.', time: '4 hr ago' },
  ],
  government: [
    { id: 'n1', text: 'Malaysia-China corridor recorded 126 new trade enquiries.', time: '12 min ago' },
    { id: 'n2', text: 'Halal cosmetics sector shows increased certification activity.', time: '1 hr ago' },
    { id: 'n3', text: '42 SMEs reached export-ready status this week.', time: '3 hr ago' },
    { id: 'n4', text: 'Government dashboard report generated successfully.', time: '6 hr ago' },
  ],
  admin: [
    { id: 'n1', text: 'New SME application received from Malaysia.', time: '5 min ago' },
    { id: 'n2', text: 'Investor account approved for Crescent Capital Partners.', time: '25 min ago' },
    { id: 'n3', text: 'Certificate duplicate risk flagged.', time: '1 hr ago' },
    { id: 'n4', text: 'Weekly ecosystem report generated.', time: '4 hr ago' },
  ],
};

export default function DashboardTopbar() {
  const { roleConfig } = useRole();
  const [langOpen, setLangOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [selectedLang, setSelectedLang] = useState('EN');

  const notifications = ROLE_NOTIFICATIONS[roleConfig.id] || ROLE_NOTIFICATIONS['investor'];
  const initials = roleConfig.profileName.split(' ').map(w => w[0]).slice(0, 2).join('');

  return (
    <header
      className="flex items-center justify-between px-6 py-3 border-b shrink-0"
      style={{ background: 'var(--secondary)', borderColor: 'var(--border)', height: '64px' }}
    >
      {/* Search */}
      <div className="flex items-center gap-3 flex-1 max-w-md">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--muted-foreground)' }} />
          <input
            type="text"
            placeholder="Search certificates, companies, opportunities..."
            className="w-full pl-9 pr-4 py-2 rounded-lg text-sm input-field"
          />
        </div>
      </div>

      {/* Right Actions */}
      <div className="flex items-center gap-2">
        {/* Role Badge */}
        <div
          className="hidden md:flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold"
          style={{ background: `${roleConfig.color}15`, border: `1px solid ${roleConfig.color}30`, color: roleConfig.color }}
        >
          {roleConfig.accountLabel}
        </div>

        {/* Language */}
        <div className="relative">
          <button
            onClick={() => { setLangOpen(!langOpen); setNotifOpen(false); setProfileOpen(false); }}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm btn-outline"
          >
            <Globe size={14} />
            <span>{selectedLang}</span>
            <ChevronDown size={12} />
          </button>
          {langOpen && (
            <div className="absolute right-0 top-full mt-2 w-40 rounded-xl shadow-card border z-50 py-1" style={{ background: 'var(--card)', borderColor: 'var(--border)' }}>
              {languages.map((lang) => (
                <button
                  key={`lang-${lang.code}`}
                  onClick={() => { setSelectedLang(lang.code); setLangOpen(false); }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm transition-colors hover:bg-muted"
                  style={{ color: selectedLang === lang.code ? 'var(--emerald-bright)' : 'var(--foreground)' }}
                >
                  <span className="font-mono text-xs font-semibold" style={{ color: 'var(--muted-foreground)' }}>{lang.code}</span>
                  <span>{lang.label}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Notifications */}
        <div className="relative">
          <button
            onClick={() => { setNotifOpen(!notifOpen); setLangOpen(false); setProfileOpen(false); }}
            className="relative p-2 rounded-lg btn-outline"
          >
            <Bell size={18} />
            <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full" style={{ background: 'var(--gold)' }} />
          </button>
          {notifOpen && (
            <div className="absolute right-0 top-full mt-2 w-80 rounded-xl shadow-card border z-50" style={{ background: 'var(--card)', borderColor: 'var(--border)' }}>
              <div className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: 'var(--border)' }}>
                <span className="text-sm font-semibold">Notifications</span>
                <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: 'rgba(10,107,79,0.2)', color: 'var(--emerald-bright)' }}>
                  {notifications.length} new
                </span>
              </div>
              <div className="py-2 max-h-72 overflow-y-auto scrollbar-thin">
                {notifications.map((n) => (
                  <div key={n.id} className="px-4 py-3 border-b cursor-pointer transition-colors hover:bg-muted" style={{ borderColor: 'var(--border)' }}>
                    <p className="text-xs leading-relaxed" style={{ color: 'var(--foreground)' }}>{n.text}</p>
                    <p className="text-xs mt-1" style={{ color: 'var(--muted-foreground)' }}>{n.time}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Profile */}
        <div className="relative">
          <button
            onClick={() => { setProfileOpen(!profileOpen); setLangOpen(false); setNotifOpen(false); }}
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg btn-outline"
          >
            <div
              className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold"
              style={{ background: `linear-gradient(135deg, ${roleConfig.color}, var(--emerald-deep))`, color: 'white' }}
            >
              {initials}
            </div>
            <div className="text-left hidden sm:block">
              <p className="text-xs font-semibold leading-none truncate max-w-[120px]" style={{ color: 'var(--foreground)' }}>{roleConfig.profileName}</p>
              <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>{roleConfig.title}</p>
            </div>
            <ChevronDown size={12} />
          </button>
          {profileOpen && (
            <div className="absolute right-0 top-full mt-2 w-52 rounded-xl shadow-card border z-50 py-1" style={{ background: 'var(--card)', borderColor: 'var(--border)' }}>
              <div className="px-3 py-2 border-b" style={{ borderColor: 'var(--border)' }}>
                <p className="text-sm font-semibold truncate">{roleConfig.profileName}</p>
                <p className="text-xs" style={{ color: 'var(--muted-foreground)' }}>{roleConfig.title}</p>
              </div>
              <button className="w-full flex items-center gap-2 px-3 py-2 text-sm transition-colors hover:bg-muted">
                <Settings size={14} style={{ color: 'var(--muted-foreground)' }} />
                Settings
              </button>
              <Link
                href="/login-role-selection"
                className="w-full flex items-center gap-2 px-3 py-2 text-sm transition-colors hover:bg-muted"
                style={{ color: 'var(--danger)' }}
              >
                <LogOut size={14} />
                Sign Out
              </Link>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}